import { Device, Sensor, Sensorreadertype } from "@inductosense/typescript-fetch";

/**
 * Validates if a sensor is compatible with a specific device based on device reader type
 * @param sensor The sensor to validate
 * @param device The device to validate against
 * @returns true if the sensor is compatible with the device, false otherwise
 */
export function isSensorCompatibleWithDevice(sensor: Sensor, device: Device): boolean {
    // For RemoteDataCollectorRubber devices, only allow sensors with ProductName "B06C"
    if (device.readerType === Sensorreadertype.RemoteDataCollectorRubber) {
        return sensor.sensorType?.productName === "B06C";
    }
    
    // For DataCollector (HDC) devices, exclude B06C sensors
    if (device.readerType === Sensorreadertype.DataCollector) {
        return sensor.sensorType?.productName !== "B06C";
    }
    
    // For RemoteDataCollector (regular RDC) devices, exclude B06C sensors
    if (device.readerType === Sensorreadertype.RemoteDataCollector) {
        return sensor.sensorType?.productName !== "B06C";
    }
    
    // For all other device types, allow all sensors
    return true;
}

/**
 * Filters a list of sensors to only include those compatible with the given device
 * @param sensors Array of sensors to filter
 * @param device The device to validate compatibility against
 * @returns Array of sensors that are compatible with the device
 */
export function filterSensorsForDevice(sensors: Sensor[], device: Device): Sensor[] {
    return sensors.filter(sensor => isSensorCompatibleWithDevice(sensor, device));
}

/**
 * Gets allowed RFID prefixes for a device based on compatibility rules
 * @param device The device to get allowed prefixes for
 * @returns Array of allowed RFID prefixes, or undefined to allow all sensors
 */
export function getAllowedRfidPrefixesForDevice(device: Device): string[] | undefined {
    // For RemoteDataCollectorRubber devices, only allow B06C sensors (RFID prefix EEE004)
    if (device.readerType === Sensorreadertype.RemoteDataCollectorRubber) {
        return ["EEE004"]; // B06C sensors only
    }
    
    // For DataCollector (HDC) and RemoteDataCollector (regular RDC) devices, 
    // exclude B06C sensors by allowing all other RFID prefixes
    if (device.readerType === Sensorreadertype.DataCollector || 
        device.readerType === Sensorreadertype.RemoteDataCollector) {
        return ["EEE0", "EEE1"]; // All sensor types except B06C (EEE004)
    }
    
    // For all other device types, allow all sensors (return undefined)
    return undefined;
}

/**
 * Gets an error message explaining why a sensor is not compatible with a device
 * @param sensor The sensor that is not compatible
 * @param device The device it's not compatible with
 * @returns Error message string
 */
export function getSensorIncompatibilityMessage(sensor: Sensor, device: Device): string {
    if (device.readerType === Sensorreadertype.RemoteDataCollectorRubber) {
        return `Sensor "${sensor.description || sensor.rfid}" with product type "${sensor.sensorType?.productName || "Unknown"}" is not compatible with RemoteDataCollectorRubber devices. Only sensors with product type "B06C" are supported.`;
    }
    
    if (device.readerType === Sensorreadertype.DataCollector) {
        return `Sensor "${sensor.description || sensor.rfid}" with product type "${sensor.sensorType?.productName || "Unknown"}" is not compatible with DataCollector (HDC) devices. B06C sensors are not supported.`;
    }
    
    if (device.readerType === Sensorreadertype.RemoteDataCollector) {
        return `Sensor "${sensor.description || sensor.rfid}" with product type "${sensor.sensorType?.productName || "Unknown"}" is not compatible with this RemoteDataCollector device. B06C sensors are not supported.`;
    }
    
    return `Sensor "${sensor.description || sensor.rfid}" is not compatible with this device.`;
}

/**
 * Gets a user-friendly message for device-specific sensor filtering
 * @param device The device to get the message for
 * @returns Message string or undefined if no special filtering applies
 */
export function getDeviceSensorFilterMessage(device: Device): string | undefined {
    if (device.readerType === Sensorreadertype.RemoteDataCollectorRubber) {
        return "Only B06C sensors can be selected for Rubber RDC devices.";
    }
    
    if (device.readerType === Sensorreadertype.DataCollector) {
        return "B06C sensors cannot be selected for HDC devices.";
    }
    
    if (device.readerType === Sensorreadertype.RemoteDataCollector) {
        return "B06C sensors cannot be selected for this RDC device.";
    }
    
    return undefined;
}
